import {canvasFont, fontSize} from "@/modules/canvas/styles.js"

function nextSpecialIndex(line, i) {
    let best = line.length
    const bold = line.indexOf("**", i)
    const tick = line.indexOf("`", i)
    if (bold !== -1) best = Math.min(best, bold)
    if (tick !== -1) best = Math.min(best, tick)
    return best
}

/**
 * Split one line into segments for **bold** and `inline code`.
 */
export function parseInlineTokens(line) {
    const tokens = []
    let i = 0
    while (i < line.length) {
        if (line.startsWith("**", i)) {
            const end = line.indexOf("**", i + 2)
            if (end !== -1) {
                tokens.push({text: line.slice(i + 2, end), bold: true})
                i = end + 2
                continue
            }
        }
        if (line[i] === "`") {
            const end = line.indexOf("`", i + 1)
            if (end !== -1) {
                tokens.push({text: line.slice(i + 1, end), code: true})
                i = end + 1
                continue
            }
        }
        const next = nextSpecialIndex(line, i)
        const plain = line.slice(i, next === i ? i + 1 : next)
        if (plain) tokens.push({text: plain})
        i = next === i ? i + 1 : next
    }
    return tokens
}

function* wordsFromTokens(tokens) {
    for (const t of tokens) {
        const parts = t.text.split(/(\s+)/)
        for (const p of parts) {
            if (p) yield {text: p, bold: t.bold, code: t.code}
        }
    }
}

function segmentFont({bold, code}) {
    if (code) return `${fontSize}px ui-monospace, "Cascadia Code", Consolas, monospace`
    if (bold) return `bold ${fontSize}px Roboto, "Helvetica Neue", Arial, sans-serif`
    return canvasFont
}

/**
 * @returns {Array<Array<{text: string, bold?: boolean, code?: boolean}>>}
 */
export function wrapNoteMarkdownLines(ctx, content, maxWidth) {
    const paragraphs = String(content ?? "").split("\n")
    const allLines = []

    for (const para of paragraphs) {
        const tokens = parseInlineTokens(para)
        let line = []
        let lineWidth = 0

        for (const word of wordsFromTokens(tokens)) {
            const isOnlySpace = /^\s+$/.test(word.text)
            ctx.font = segmentFont(word)
            const w = ctx.measureText(word.text).width

            if (!isOnlySpace && lineWidth + w > maxWidth && line.length) {
                allLines.push(line)
                line = []
                lineWidth = 0
            }

            if (isOnlySpace && !line.length) continue

            line.push(word)
            lineWidth += w
        }

        if (line.length) allLines.push(line)
        else if (para === "") allLines.push([{text: ""}])
    }

    return allLines
}

export function measureNoteMarkdownWidth(ctx, lines) {
    let maxW = 0
    for (const segs of lines) {
        let w = 0
        for (const seg of segs) {
            ctx.font = segmentFont(seg)
            w += ctx.measureText(seg.text).width
        }
        maxW = Math.max(maxW, w)
    }
    return maxW
}

export {segmentFont}
